import { DownOutlined } from '@ant-design/icons';
import { Button, Dropdown, List, DatePicker, Divider } from 'antd';
import { Selectable } from 'app/components/Selectable';
import React, { useEffect, useMemo, useState } from 'react';
import classes from './Sorter.module.scss';
import { RangeValue } from 'rc-picker/lib/interface';
import moment from 'moment';
import { isNil } from 'lodash';

const { RangePicker } = DatePicker;

type SortValue = {
  sortBy: string;
  direction?: string | null;
  ranges?: RangeValue<moment.Moment>;
};
type Props = {
  options: { value: string; label: string }[];
  sortByOptions?: { value: string; label: string }[];
  icon: React.ReactNode;
  label:
    | React.ReactNode
    | ((
        selected: any[],
        sortBy: string,
        range: RangeValue<moment.Moment>,
        config: Props
      ) => React.ReactNode);
  value?: SortValue;
  onChange?: (value: SortValue) => void;
  allowRangePicker?: boolean;
};

export default function Sorter(props: Props) {
  const {
    label,
    icon,
    options,
    allowRangePicker,
    value,
    onChange,
    sortByOptions
  } = props;
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [range, setRange] = useState<RangeValue<moment.Moment> | null>(null);

  const transformOptions = useMemo(() => {
    if (!options || !options.length) {
      return [];
    }
    return options.map((option) => {
      return {
        key: option.value,
        label: option.label
      };
    });
  }, [options]);

  const handleSelect = (direction: string) => (checked) => {
    if (checked) {
      setSelected(direction);
    }
  };

  const handleSelectSortBy = (sortBy: string) => (checked) => {
    if (checked) {
      setSortBy(sortBy);
    }
  };

  const handleChangeRange = (value: RangeValue<moment.Moment>) => {
    setRange(value);
  };

  const handleApply = () => {
    if (allowRangePicker && !isNil(range)) {
      onChange({
        sortBy,
        direction: selected,
        ranges: range
      });
      setOpen(false);
      return;
    }
    onChange({
      sortBy,
      direction: selected,
      ranges: null
    });
    setOpen(false);
  };

  const handleReset = () => {
    onChange(null);
    setOpen(false);
  };

  const handleClearState = () => {
    if (isNil(value)) {
      setSortBy('');
      setSelected('');
      setRange(null);
      return;
    }
    const { sortBy, ranges, direction } = value;
    setSortBy(sortBy);
    setSelected(direction);
    setRange(null);
    if (Array.isArray(ranges) && ranges.length > 1) {
      setRange(ranges);
    }
  };

  useEffect(() => {
    handleClearState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, open]);

  return (
    <Dropdown
      trigger={['click']}
      menu={{ items: transformOptions }}
      onOpenChange={(visible) => {
        setOpen(visible);
      }}
      open={open}
      dropdownRender={() => {
        return (
          <div className={classes.list}>
            {Array.isArray(sortByOptions) && (
              <>
                <List
                  split={false}
                  dataSource={sortByOptions}
                  renderItem={(sortByOption) => {
                    return (
                      <List.Item>
                        <Selectable
                          onChange={handleSelectSortBy(sortByOption.value)}
                          checked={sortByOption.value === sortBy}
                        >
                          {sortByOption.label}
                        </Selectable>
                      </List.Item>
                    );
                  }}
                ></List>
                <Divider className={classes.divider} />
              </>
            )}
            <List
              split={false}
              dataSource={transformOptions}
              renderItem={(option) => {
                return (
                  <List.Item>
                    <Selectable
                      onChange={handleSelect(option.key)}
                      checked={option.key === selected}
                    >
                      {option.label}
                    </Selectable>
                  </List.Item>
                );
              }}
            ></List>
            <div
              hidden={!allowRangePicker}
              className={classes.contentRangePicker}
            >
              <RangePicker
                value={range}
                format={'DD/MM/YYYY'}
                placeholder={['From', 'To']}
                onChange={(values) => {
                  handleChangeRange(values);
                }}
              ></RangePicker>
            </div>
            <div className={classes.footer}>
              <Button type="primary" onClick={handleApply}>
                Apply
              </Button>
              <Button onClick={handleReset}>Reset</Button>
            </div>
          </div>
        );
      }}
    >
      <Button className={classes.dropdownBtn} type="text" icon={icon}>
        {typeof label === 'function' ? label([], sortBy, range, props) : label}
        <DownOutlined />
      </Button>
    </Dropdown>
  );
}
